/* eslint-disable @nx/enforce-module-boundaries */
import {
  Avatar,
  AvatarGroup,
  Box,
  Button,
  Divider,
  HStack,
  Heading,
  Link,
  LinkButton,
  SeeMore,
  Stack,
  Text,
  Tooltip,
  IconButton,
} from '@mybridge/ui';
import { forwardRef, useContext, useEffect, useRef, useState } from 'react';
import { MyProfileContext } from '../context';
import moment from 'moment';
import { EMPLOYMENT_CHOICES } from 'v4/lib/data';
import {
  AddCircle,
  HandShakeIcon,
  HandShakeSimpleIcon,
  EarthGlobeIcon,
  OnlyMeLockIcon,
  TeamIcon,
  AddCircleFIlled,
  NewPlusIcon,
  SimpleCloseIcon,
  ChatOutLined,
} from '@mybridge/icons';
import { ShowAllLink } from '../show-all-link';
import { EditIcon } from '@mybridge/icons/Edit';
import { useDisclosure } from '@mybridge/ui';
import { getUserFullName, getUserProfilePic } from 'v4/lib/commons';
import { MyProfileSkillsModal } from '../skills-modal';
import { isMobile } from 'react-device-detect';
import _ from 'lodash';
import { MyNetworkContext } from 'v4/components/network/context';
import { Flex, VStack } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { GlobalMatrixPopup } from 'v4/snippets/globalMatrixPopup';
import { getMatrixUserId, getRoomIdFromLS } from 'v4/utils/utils';

export const MyProfileSkills = forwardRef(({ ...props }, ref) => {
  const {
    userProfileInfo,
    skillsDisc,
    mySkills,
    refetchMySkills,
    setSelectedSkill,
    audienceDisc,
    setPermissionValue,
    setCurrentPermissionValue,
  } = useContext(MyProfileContext);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedUser, setSelectedUser] = useState(null);
  const [confirmAction, setConfirmAction] = useState(() => () => {});
  const [showAll, setShowAll] = useState(false);
  const [hoveredSkillId, setHoveredSkillId] = useState(null);
  const [loadingButtonId, setLoadingButtonId] = useState(null);
  const { data: connectionData } = useSelector((state) => state.connection);
  const triggerRef = useRef(null);
  const sectionRef = useRef(null); // Add ref for scrolling
  const router = useRouter();
  const { t } = useTranslator();

  // Add this effect to handle URL parameters
  useEffect(() => {
    // Check if we should open the skills modal based on query param
    if (router.query.openDisc === 'skills') {
      // setSelectedSkill(null);
      skillsDisc?.onOpen?.();
      
      // Clear the query parameter after opening the disclosure
      router.replace(router.pathname, undefined, { shallow: true });
    }
    
    // Check if we should scroll to this section
    if (router.query.scrollTo === 'skills' && sectionRef.current) {
      // Calculate position to scroll to (middle of viewport)
      const elementRect = sectionRef.current.getBoundingClientRect();
      const absoluteElementTop = elementRect.top + window.pageYOffset;
      const middle = absoluteElementTop - (window.innerHeight / 2) + (elementRect.height / 2);
      
      // Scroll to the calculated position
      window.scrollTo({
        top: middle,
        behavior: 'smooth'
      });
      
      // Clear the scroll parameter after scrolling
      const { scrollTo, ...rest } = router.query;
      router.replace(
        { pathname: router.pathname, query: rest },
        undefined,
        { shallow: true }
      );
    }
  }, [router.query, skillsDisc, setSelectedSkill]);

  // const getMatrixUserId = (userId) => {
  //   const connection = connectionData?.results?.find(conn => conn?.id === userId);
  //   return connection?.matrix_user_id;
  // };
  const { 
    isFriend,
    handshakeUser,
    followUser,
    isHandshakeRequested,
    isFollowed,
    handshakeLoading,
    handleHandshakeSendRevoke, 
  } = useContext(MyNetworkContext);

  const handleHandshake = async (user) => {
    setLoadingButtonId(user.id);
    try {
      await handshakeUser?.(user);
    } finally {
      setLoadingButtonId(null);
    }
  };
  const cardRefs = useRef({});

// Function to calculate position for each popup
const getPopupPosition = (skillId) => {
  const cardElement = cardRefs.current[skillId];
  if (!cardElement) return {};
  
  const rect = cardElement.getBoundingClientRect();
  const viewportHeight = window.innerHeight;
  const spaceAbove = rect.top;
  const spaceBelow = viewportHeight - rect.bottom;
  
  // Position above if enough space, otherwise below
  if (spaceAbove >= 400) {
    return {
      bottom: viewportHeight - rect.top + 8,
      left: rect.left
    };
  }
  
  return {
    top: rect.bottom + 8,
    left: rect.left
  };
};

  const skillsValue = _.get(userProfileInfo, 'field_audience.skills');

  const renderPermissionSwitch = (param) => {
    switch (param) {
      case 1:
        return {
          icon: <EarthGlobeIcon width="16px" height="16px" />,
          text: t('profileDetails.public') || 'Public',
        };
      case 2:
        return {
          icon: <TeamIcon width="16px" height="16px" />,
          text: t('profileDetails.myNetwork') || 'myNetwork',
        };
      case 3:
        return {
          icon: <OnlyMeLockIcon width="16px" height="16px" />,
          text: t('profileDetails.onlyMe') || 'Only me',
        };
      default:
        return {
          icon: <EarthGlobeIcon width="16px" height="16px" />,
          text: t('profileDetails.public') || 'Public',
        };
    }
  };

  const timerRef = useRef(null);

  const handleMouseEnter = (skill) => {
    if (skill?.agreed_user?.length > 0) {
      clearTimeout(timerRef.current);
      setHoveredSkillId(skill.id);
    }
  };

  const handleMouseLeave = () => {
    timerRef.current = setTimeout(() => {
      setHoveredSkillId(null);
    }, 1000);
  };

  return (
    <>
      {skillsDisc?.isOpen ? (
        <MyProfileSkillsModal />
      ) : (
        <Stack ref={sectionRef} {...props}>
          <HStack alignItems="flex-start">
            <Stack flex={1}>
              <Heading fontSize="18px" color="brandPrimary.500" fontWeight="400">{t('profileSkills.skills') || "Skills"}</Heading>
            </Stack>
            <HStack justifyContent="flex-end">
              {mySkills?.length && (
                <Button
                  leftIcon={<AddCircle />}
                  size={['sm', 'md']}
                  variant="transparent"
                  color="brandPrimary.500"
                  onClick={(e) => skillsDisc?.onOpen?.()}
                >
                  {t('profileSkills.addSkills') || "Add skills"}
                </Button>
              )}
              <HStack>
                <Tooltip label={renderPermissionSwitch(skillsValue)?.text}>
                  <IconButton
                    icon={renderPermissionSwitch(skillsValue)?.icon}
                    size="sm"
                    variant="transparent"
                    onClick={() => {
                      setCurrentPermissionValue(skillsValue);
                      audienceDisc.onOpen();
                      setPermissionValue('skills');
                    }}
                  />
                </Tooltip>
              </HStack>
            </HStack>
          </HStack>

          {mySkills?.length ? (
            <>
              <HStack alignItems="flex-start" flexWrap={'wrap'} pt={4} spacing={0} gap={4}>
                {mySkills?.filter?.((_, ind) => showAll || ind < 4)?.map?.((skill, skillIndex) => (
                  <Stack
                    key={skillIndex}
                    ref={el => cardRefs.current[skill.id] = el}
                    px={4}
                    py={2}
                    bg="brandGray.500"
                    borderRadius={10}
                    spacing={0}
                    gap={1}
                    // ref={triggerRef}
                    // onMouseEnter={() => setHoveredSkillId(skill.id)}
                    // onMouseLeave={() => setHoveredSkillId(null)}
                    onMouseEnter={() => handleMouseEnter(skill)}  // Pass the entire skill object
                    onMouseLeave={handleMouseLeave}
                  >
                    <Text>{skill.name}</Text>
                    <HStack>
                      <Box opacity={0.6}>
                        <HandShakeSimpleIcon width={20} height={20} color="green" />
                      </Box>
                      <HStack>
                        <AvatarGroup>
                          {skill?.agreed_user?.filter?.((_, ind) => ind < 3)?.map?.((u, uind) => (
                            <Avatar
                              key={uind}
                              name={getUserFullName(u)}
                              src={getUserProfilePic(u)}
                              size="xs"
                              as={Link}
                              href={'/p/' + u?.slug}
                            />
                          ))}
                        </AvatarGroup>
                        <Text fontSize="xs">
                          {skill?.agreed_user?.length > 3 ? `+${skill.agreed_user?.length - 3} ` : ''} {t('post.agreed') || "Agreed"}
                        </Text>
                      </HStack>
                    </HStack>

                    {hoveredSkillId === skill.id && (
                      <Box
                        position="fixed"
                        w="400px"
                        bg="white"
                        boxShadow="lg"
                        borderRadius="8px"
                        p={4}
                        pt={0}
                        zIndex={10}
                        style={getPopupPosition(skill.id)}
                        // onMouseEnter={() => setHoveredSkillId(skill.id)}
                        // onMouseLeave={() => setHoveredSkillId(null)}
                        onMouseEnter={() => clearTimeout(timerRef.current)}
                        onMouseLeave={handleMouseLeave}
                      >
                        <Box position="relative" border="transparent">
                          <Divider mb={4} border="transparent"/>
                          <Box>
                            <Flex justifyContent="space-between" alignItems="center" mb={4}>
                              <Text fontWeight="400" fontSize="20px" color="#3D5A80" display="flex" justifyContent="center" width="100%">
                                {t('profileSkills.agreedUsers') || "Agreed Users"}
                              </Text>
                              <IconButton
                                display={"flex"}
                                icon={<SimpleCloseIcon height="14px" width="14px" />}
                                aria-label="Close"
                                variant={'unstyled'}
                                onClick={() => setHoveredSkillId(null)}
                              />
                            </Flex>
                            <Box maxHeight="300px" overflowY="auto" p={2}>
                              <VStack spacing={4} align="stretch">
                                {skill?.agreed_user?.map((user, index) => (
                                  <Flex key={index} alignItems="center" justifyContent="space-between">
                                    <HStack spacing={3} cursor="pointer" onClick={() => userProfileInfo?.id === user?.id ? router.push(`/profile/me`) : router.push(`/p/${user?.slug}`)}>
                                      <Avatar src={user?.profile_pic} name={getUserFullName(user)} />
                                      <Text fontWeight="500" color="#5B5B5B" fontSize="15px">{getUserFullName(user)}</Text>
                                    </HStack>
                                    <HStack spacing={2}>
                                      {userProfileInfo?.id === user?.id ? null :
                                        !isFriend(user) ? <HStack>
                                          {isHandshakeRequested?.(user) ? (
                                            <Tooltip zIndex="99999999" label={t('publicProfile.withdrawHandshake') || "Withdraw Handshake"}>
                                              <IconButton
                                                onClick={(e) => handleHandshakeSendRevoke?.(user)}
                                                _hover={{ bg: 'gray.100' }}
                                                isLoading={handshakeLoading}
                                                icon={<HandShakeSimpleIcon color="#F5AEAD" width="20" height="20" />}
                                                w={'41px'}
                                                height={'41px'}
                                                border={'1px solid #00000033'}
                                                cursor="pointer"
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="center"
                                                borderRadius={'50%'}
                                                variant="none"
                                              />
                                            </Tooltip>
                                          ) : (
                                            <Tooltip zIndex="99999999" label={t('publicProfile.sendHandshakeRequest') || "Send Handshake Request"}>
                                              <IconButton
                                                w={'41px'}
                                                _hover={{ bg: 'gray.100' }}
                                                height={'41px'}
                                                border={'1px solid #00000033'}
                                                cursor="pointer"
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="center"
                                                borderRadius={'50%'}
                                                variant="none"
                                                onClick={() => handleHandshake(user)}
                                                isLoading={loadingButtonId === user.id}
                                                icon={<HandShakeSimpleIcon color="#037F29" width="20" height="20" />}
                                              />
                                            </Tooltip>
                                          )}
                                        </HStack> :
                                        <Box
                                          w={'41px'}
                                          height={'41px'}
                                          border={'1px solid #00000033'}
                                          cursor="pointer"
                                          display="flex"
                                          alignItems="center"
                                          justifyContent="center"
                                          borderRadius={'50%'}
                                          // onClick={(e) => {
                                          //   const matrixUserId = getMatrixUserId(user.id);
                                          //   router.push(`/profile/message?user=${matrixUserId}`);
                                          // }}
                                          onClick={() => {
                                            // const matrixUserId = getMatrixUserId(user?.id)
                                                     
                                            // if (!(user?.connection?.chat_room_id || matrixUserId)) {
                                            //   setConfirmAction(() => (newId) => {
                                            //     router.push(`/profile/message?user=${newId}`);
                                            //   });
                                            //   onOpen();
                                            // } else {
                                            //   router.push(`/profile/message?user=${user?.matrix_user_id || matrixUserId}`);
                                            // }
                                            const roomId = getRoomIdFromLS(user?.id);
                                            router.push(`/profile/message?user=${roomId}`);
                                          }}
                                          _hover={{ bg: 'gray.100' }}
                                        >
                                          <ChatOutLined/>
                                        </Box>
                                      }
                                    </HStack>
                                  </Flex>
                                ))}
                              </VStack>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    )}
                  </Stack>
                ))}
              </HStack>
              {!showAll && mySkills?.length > 4 && (
                <ShowAllLink onClick={(e) => setShowAll(true)} />
              )}
            </>
          ) : (
            <HStack justifyContent="flex-start">
              {isMobile ? (
                <Button
                  leftIcon={null}
                  width="100%"
                  minW={isMobile && "93vw"}
                  justifyContent="space-between"
                  variant="transparent"
                  color={isMobile ? '#747474' : 'brandPrimary.500'}
                  onClick={(e) => skillsDisc?.onOpen?.()}
                  pl={0}
                >
                  <Box opacity={0.5}>{t('profileSkills.addSkills') || "Add skills"}</Box>
                  <NewPlusIcon width="20" height="20" />
                </Button>
              ) : (
                <Button
                  leftIcon={<AddCircleFIlled />}
                  variant="transparent"
                  color="brandPrimary.500"
                  onClick={(e) => skillsDisc?.onOpen?.()}
                  pl={0}
                >
                  <Box opacity={0.5}>{t('profileSkills.addSkills') || "Add skills"}</Box>
                </Button>
              )}
            </HStack>
          )}
        </Stack>
      )}
      <GlobalMatrixPopup
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={confirmAction}
        selectedUserId={selectedUser?.id}
      />
    </>
  );
});
